import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { 
  RequestConfig, 
  ApiError, 
  FileUploadConfig,
  PaginatedResponse 
} from './types';
import { ErrorHandler } from './errorHandler';

export abstract class BaseModule {
  protected httpClient: AxiosInstance;
  protected errorHandler: ErrorHandler;

  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    this.httpClient = httpClient;
    this.errorHandler = errorHandler;
  }

  /**
   * Generic GET request
   */
  protected async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    try {
      const response = await this.httpClient.get<T>(endpoint, config);
      return response.data;
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Generic POST request
   */
  protected async post<T>(
    endpoint: string, 
    data?: any, 
    config?: RequestConfig
  ): Promise<T> {
    try {
      const response = await this.httpClient.post<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Generic PUT request
   */
  protected async put<T>(
    endpoint: string, 
    data?: any, 
    config?: RequestConfig
  ): Promise<T> {
    try {
      const response = await this.httpClient.put<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Generic PATCH request
   */
  protected async patch<T>(
    endpoint: string, 
    data?: any, 
    config?: RequestConfig
  ): Promise<T> {
    try {
      const response = await this.httpClient.patch<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Generic DELETE request
   */
  protected async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    try {
      const response = await this.httpClient.delete<T>(endpoint, config);
      return response.data;
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * File upload with progress tracking
   */
  protected async uploadFile<T>(
    endpoint: string,
    file: File,
    config?: FileUploadConfig
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    // Validate file if constraints are provided
    if (config?.maxFileSize && file.size > config.maxFileSize) {
      throw this.errorHandler.handleError(
        new Error(`File size exceeds maximum allowed size of ${config.maxFileSize} bytes`)
      );
    }

    if (config?.allowedTypes && !config.allowedTypes.includes(file.type)) {
      throw this.errorHandler.handleError(
        new Error(`File type ${file.type} is not allowed`)
      );
    }

    const requestConfig: AxiosRequestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (config?.onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          config.onProgress(progress);
        }
      },
    };

    try {
      const response = await this.httpClient.post<T>(endpoint, formData, requestConfig);
      return response.data;
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Multiple file upload
   */
  protected async uploadFiles<T>(
    endpoint: string,
    files: File[],
    config?: FileUploadConfig
  ): Promise<T> {
    const formData = new FormData();
    
    files.forEach((file, index) => {
      // Validate each file if constraints are provided
      if (config?.maxFileSize && file.size > config.maxFileSize) {
        throw this.errorHandler.handleError(
          new Error(`File ${file.name} exceeds maximum allowed size`)
        );
      }

      if (config?.allowedTypes && !config.allowedTypes.includes(file.type)) {
        throw this.errorHandler.handleError(
          new Error(`File type ${file.type} is not allowed for ${file.name}`)
        );
      }

      formData.append(`files[${index}]`, file);
    });

    const requestConfig: AxiosRequestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (config?.onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          config.onProgress(progress);
        }
      },
    };

    try {
      const response = await this.httpClient.post<T>(endpoint, formData, requestConfig);
      return response.data;
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Download file as blob
   */
  protected async downloadFile(endpoint: string, config?: RequestConfig): Promise<Blob> {
    try {
      const response = await this.httpClient.get(endpoint, {
        ...config,
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Paginated GET request
   */
  protected async getPaginated<T>(
    endpoint: string,
    page: number = 1,
    limit: number = 20,
    config?: RequestConfig
  ): Promise<PaginatedResponse<T>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add existing params from config
    if (config?.params) {
      Object.entries(config.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const url = `${endpoint}?${params.toString()}`;

    try {
      const response = await this.httpClient.get<PaginatedResponse<T>>(url, {
        ...config,
        params: undefined, // Remove params since we're adding them to URL
      });
      return response.data;
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Request with retry logic
   */
  protected async requestWithRetry<T>(
    requestFn: () => Promise<T>,
    maxRetries: number = 3
  ): Promise<T> {
    let lastError: ApiError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = this.errorHandler.handleError(error);
        
        if (attempt === maxRetries || !this.errorHandler.shouldRetry(lastError, attempt)) {
          throw lastError;
        }

        // Wait before retrying
        const delay = this.errorHandler.getRetryDelay(attempt);
        await this.sleep(delay);
      }
    }

    throw lastError!;
  }

  /**
   * Build query string from parameters
   */
  protected buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(item => searchParams.append(key, item.toString()));
        } else {
          searchParams.append(key, value.toString());
        }
      }
    });

    return searchParams.toString();
  }

  /**
   * Transform request data before sending
   */
  protected transformRequest(data: any): any {
    if (!data) return data;

    // Handle Date objects
    if (data instanceof Date) {
      return data.toISOString();
    }

    // Handle objects with Date properties
    if (typeof data === 'object' && data !== null) {
      const transformed = { ...data };
      
      Object.keys(transformed).forEach(key => {
        if (transformed[key] instanceof Date) {
          transformed[key] = transformed[key].toISOString();
        }
      });

      return transformed;
    }

    return data;
  }

  /**
   * Transform response data after receiving
   */
  protected transformResponse<T>(data: T): T {
    if (!data) return data;

    // Handle arrays
    if (Array.isArray(data)) {
      return data.map(item => this.transformResponse(item)) as T;
    }

    // Handle objects
    if (typeof data === 'object' && data !== null) {
      const transformed = { ...data } as any;
      
      Object.keys(transformed).forEach(key => {
        const value = transformed[key];
        
        // Transform ISO date strings back to Date objects
        if (typeof value === 'string' && this.isISODateString(value)) {
          transformed[key] = new Date(value);
        } else if (typeof value === 'object') {
          transformed[key] = this.transformResponse(value);
        }
      });

      return transformed;
    }

    return data;
  }

  /**
   * Check if string is an ISO date string
   */
  private isISODateString(value: string): boolean {
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
    return isoDateRegex.test(value) && !isNaN(Date.parse(value));
  }

  /**
   * Sleep utility for retry delays
   */
  protected sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}